import { useMemo } from "react";
import { Users } from "lucide-react";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { useChatStore } from "@/features/chat/store/chat-store";
import type { Conversation } from "@/features/chat/types";
import { conversationTitle, initials, otherMember } from "@/features/chat/utils";

/**
 * Right-hand context panel for the open conversation. Real content only
 * (person header, live team-online avatars, retention note) — the mockup's
 * Call / Assign-task buttons and "Linked in this chat" items have no backing
 * data or actions, so they're intentionally omitted.
 */
export function ConversationContextPanel({ conversation }: { conversation: Conversation }) {
  const meId = useAuthStore((s) => s.user?.id ?? null);
  const conversations = useChatStore((s) => s.conversations);
  const onlineIds = useChatStore((s) => s.onlineIds);

  const title = conversationTitle(conversation, meId);
  const isGroup = conversation.kind === "group";
  const other = otherMember(conversation, meId);

  // Teammates currently online, named from everyone we share a conversation with.
  const online = useMemo(() => {
    const names = new Map<string, string>();
    for (const c of conversations) {
      for (const m of c.members) {
        if (m.id !== meId) names.set(m.id, m.name);
      }
    }
    return onlineIds
      .filter((id) => id !== meId && names.has(id))
      .map((id) => ({ id, name: names.get(id) as string }));
  }, [conversations, onlineIds, meId]);

  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto p-5">
      {/* Person / group header */}
      <div className="flex flex-col items-center gap-2 text-center">
        <span className="bg-accent text-info font-heading flex size-16 items-center justify-center rounded-xl text-xl font-bold">
          {isGroup ? <Users className="size-7" /> : initials(title)}
        </span>
        <span className="font-heading text-[17px] font-semibold tracking-[-0.01em]">{title}</span>
        {isGroup ? (
          <span className="text-muted-foreground text-xs">
            {conversation.members.length} members
          </span>
        ) : (
          other?.email && (
            <span className="text-muted-foreground text-xs break-all">{other.email}</span>
          )
        )}
      </div>

      {/* Team online */}
      {online.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
            Team online
          </span>
          <div className="flex flex-wrap gap-1.5">
            {online.map((m) => (
              <span
                key={m.id}
                title={m.name}
                className="bg-accent text-info font-heading relative flex size-[30px] items-center justify-center rounded-[9px] text-[10px] font-bold"
              >
                {initials(m.name)}
                <span className="bg-success border-card absolute -right-0.5 -bottom-0.5 size-[9px] rounded-full border-2" />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Retention note — static firm policy chrome. */}
      <div className="bg-muted/40 text-muted-foreground mt-auto rounded-md border p-3 text-xs leading-relaxed">
        <strong className="text-foreground font-semibold">Retention</strong> · messages are kept
        7 years and included in the firm's audit log.
      </div>
    </div>
  );
}
